import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  CardContent,
  Button,
  Fade,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import compact from "../../image/compact.png";
import energy from "../../image/energy.png";
import aqua from "../../image/aqua.png";
import LoginModal from "../login/LoginModal";
import { useSelector } from "react-redux";
import { brandTranslation } from "../../utils/brandUtils";
import { getValidToken } from "../../utils/tokenUtils";
import {
  HeroSection,
  ModernCard,
  ProductImage,
  CardOverlay,
  SectionTitle,
} from "./FramelessSelect.styled";

// Create a wrapper component that ensures theme is available
const FramelessSelectContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { registrationSuccess, passwordResetSuccess, recoveryEmailSent } = location.state || {};
  const theme = useTheme();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(
    Boolean(
      location.state?.registrationSuccess ||
      location.state?.passwordResetSuccess ||
      location.state?.recoveryEmailSent,
    ),
  );

  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const { isClivet } = useSelector((state) => state.brand);

  // Show login modal when user is not authenticated
  React.useEffect(() => {
    // After registration / password reset / recovery — always open login
    if (
      registrationSuccess ||
      passwordResetSuccess ||
      recoveryEmailSent
    ) {
      setIsLoginModalOpen(true);
      return;
    }

    if (isLoggedIn) {
      setIsLoginModalOpen(false);
      return;
    }

    // Check for a valid (non-expired) token — restoreAuth may still be processing
    const validToken = getValidToken();
    if (validToken) {
      // Valid token exists, wait briefly for restoreAuth to finish
      return;
    }

    // No valid token — show login modal after small delay
    // (prevents race condition with Google password manager autofill)
    const timer = setTimeout(() => {
      setIsLoginModalOpen(true);
    }, 150);

    return () => clearTimeout(timer);
  }, [isLoggedIn, location.state, registrationSuccess, passwordResetSuccess, recoveryEmailSent]);


  const handleDashboardClick = () => {
    navigate("/dashboard");
  };

  return (
    <>
      <Box
        sx={{
          filter: isLoginModalOpen ? "blur(8px)" : "none",
          transition: "filter 0.3s ease",
          pointerEvents: isLoginModalOpen ? "none" : "auto",
          minHeight: "100vh",
          fontFamily:
            "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        {/* Hero Section */}
        <HeroSection>
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
            <Fade in timeout={800}>
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontSize: { xs: "2.5rem", md: "4rem", lg: "4rem" },
                    fontWeight: 900,
                    color: "white",
                    mb: 3,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                    textShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                  }}
                >
                  {t(brandTranslation("framelessSelect.welcomeTitle"))}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                    maxWidth: "800px",
                    mx: "auto",
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                    lineHeight: 1.6,
                    fontWeight: 400,
                    textShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  {t(brandTranslation("framelessSelect.welcomeDescription"))}
                </Typography>
              </Box>
            </Fade>
          </Container>
        </HeroSection>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <SectionTitle
              variant="h3"
              component="h2"
              sx={{
                fontSize: { xs: "2rem", md: "3rem" },
                mb: 2,
              }}
            >
              {t(brandTranslation("framelessSelect.getStartedTitle"))}
            </SectionTitle>
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                maxWidth: "600px",
                mx: "auto",
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
              {t(brandTranslation("framelessSelect.chooseAhuType"))}
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {/* Compact AHU Card */}
            <Grid item xs={12} md={6} lg={4}>
              <Fade in timeout={1600}>
                <ModernCard onClick={handleDashboardClick}>
                  <ProductImage>
                    <img
                      src={compact}
                      alt="Compact Prime:Pro"
                      className="product-image"
                    />
                  </ProductImage>
                  <CardContent
                    sx={{
                      p: 4,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                          fontWeight: 800,
                          mb: 2,
                          color: theme.palette.dark.main,
                          textAlign: "center",
                        }}
                      >
                        {t(
                          brandTranslation(
                            "framelessSelect.productCards.compactAhu.title"
                          )
                        )}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "text.secondary",
                          textAlign: "center",
                          lineHeight: 1.6,
                          mb: 3,
                        }}
                      >
                        {t(
                          brandTranslation(
                            "framelessSelect.productCards.compactAhu.description"
                          )
                        )}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      sx={{
                        borderRadius: "12px",
                        py: 1.5,
                        background: `linear-gradient(135deg, ${theme.palette.primary.other}, ${theme.palette.secondary.main})`,
                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                        "&:hover": {
                          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.2)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {t(
                        brandTranslation(
                          "framelessSelect.productCards.compactAhu.buttonText"
                        )
                      )}
                    </Button>
                  </CardContent>
                </ModernCard>
              </Fade>
            </Grid>

            {/* Energy AHU Card */}
            <Grid item xs={12} md={6} lg={4}>
              <Fade in timeout={1800}>
                <ModernCard onClick={handleDashboardClick}>
                  <ProductImage>
                    <img
                      src={energy}
                      alt="Energy Prime:Pro"
                      className="product-image"
                    />
                  </ProductImage>
                  <CardContent
                    sx={{
                      p: 4,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                          fontWeight: 800,
                          mb: 2,
                          color: theme.palette.dark.main,
                          textAlign: "center",
                        }}
                      >
                        {t(
                          brandTranslation(
                            "framelessSelect.productCards.energyAhu.title"
                          )
                        )}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "text.secondary",
                          textAlign: "center",
                          lineHeight: 1.6,
                          mb: 3,
                        }}
                      >
                        {t(
                          brandTranslation(
                            "framelessSelect.productCards.energyAhu.description"
                          )
                        )}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      sx={{
                        borderRadius: "12px",
                        py: 1.5,
                        background: `linear-gradient(135deg, ${theme.palette.primary.other}, ${theme.palette.secondary.main})`,
                        boxShadow: "0 8px 24px rgba(255, 255, 255, 1)",
                        "&:hover": {
                          boxShadow: "0 12px 32px rgba(255, 255, 255, 1)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {t(
                        brandTranslation(
                          "framelessSelect.productCards.energyAhu.buttonText"
                        )
                      )}
                    </Button>
                  </CardContent>
                </ModernCard>
              </Fade>
            </Grid>

            {/* LINE:PRIME AHU Card */}
            <Grid item xs={12} md={6} lg={4}>
              <Fade in timeout={2000}>
                <ModernCard onClick={handleDashboardClick}>
                  <ProductImage />

                  <CardContent
                    sx={{
                      p: 4,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                          fontWeight: 800,
                          mb: 2,
                          color: theme.palette.dark.main,
                          textAlign: "center",
                        }}
                      >
                        {t(brandTranslation("framelessSelect.productCards.linePrimeAhu.title"))}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "text.secondary",
                          textAlign: "center",
                          lineHeight: 1.6,
                          mb: 3,
                        }}
                      >
                        {t(brandTranslation("framelessSelect.productCards.linePrimeAhu.description"))}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      sx={{
                        borderRadius: "12px",
                        py: 1.5,
                        background: `linear-gradient(135deg, ${theme.palette.primary.other}, ${theme.palette.secondary.main})`,
                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                        "&:hover": {
                          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.2)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {t(brandTranslation("framelessSelect.productCards.linePrimeAhu.buttonText"))}
                    </Button>
                  </CardContent>
                </ModernCard>
              </Fade>
            </Grid>
          </Grid>

          {/* Call to Action */}
          <Fade in timeout={2000}>
            <Box sx={{ textAlign: "center", mt: 8 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "text.secondary",
                  maxWidth: "600px",
                  mx: "auto",
                  lineHeight: 1.6,
                  fontSize: "1.1rem",
                }}
              >
                {t(brandTranslation("framelessSelect.callToAction"))}
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Login Modal */}
      <LoginModal modalopen={isLoginModalOpen} />
    </>
  );
};

// Main component that wraps the content to ensure theme is available
const FramelessSelect = () => {
  return <FramelessSelectContent />;
};

export default FramelessSelect;
